"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { format } from "date-fns";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useWindowSize } from "usehooks-ts";
import { SLOT_PRIORITY } from "../utils/publicationDistribution";
import Button from "./Button";
import Typography from "./Typography";

// Default time for a date-only selection: the preferred hour of the
// top-priority slot (19:00), matching where the scheduler places posts.
function getBestRecommendedTime(date: Date): Date {
  const newDate = new Date(date);
  newDate.setHours(SLOT_PRIORITY[0].preferredHour, 0, 0, 0);
  return newDate;
}

export type DatePickerPopperStrategy = "absolute" | "fixed";

// Time applied when the user clicks a bare date (react-datepicker yields
// midnight). "recommended" jumps to the top slot's preferred hour (19:00) —
// right for picking a single post's datetime. "startOfDay" keeps 00:00
// (clamped to "now" when the day is today) — right for range lower bounds
// like "Start from", where 19:00 would silently exclude the day's earlier
// slots from distribution.
export type DateOnlyDefaultTime = "recommended" | "startOfDay";

export type DatePickerPlacement =
  | "auto"
  | "auto-start"
  | "auto-end"
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

type DatePickerProps = {
  onChange: (value: Date) => void;
  value?: string;
  onBlur?: () => void;
  portalId?: string;
  popperStrategy?: DatePickerPopperStrategy;
  popperPlacement?: DatePickerPlacement;
  dateOnlyDefaultTime?: DateOnlyDefaultTime;
} & Omit<ComponentPropsWithoutRef<"div">, "onChange">;

const CustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}) => (
  <div className="flex items-center justify-between  bg-white">
    <Button
      size="s"
      variant="tertiary"
      onClick={decreaseMonth}
      disabled={prevMonthButtonDisabled}
      className="p-2 hover:!bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronLeftIcon />
    </Button>
    <Typography variant="subtitle3">{format(date, "MMMM yyyy")}</Typography>
    <Button
      size="s"
      variant="tertiary"
      onClick={increaseMonth}
      disabled={nextMonthButtonDisabled}
      className="p-2 hover:!bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronRightIcon />
    </Button>
  </div>
);

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      onChange,
      value,
      onBlur,
      portalId,
      popperStrategy,
      popperPlacement,
      dateOnlyDefaultTime = "recommended",
      ...rest
    },
    ref
  ) => {
    const { width: windowWidth } = useWindowSize();
    const clampPastToNow = (d: Date) => (d.getTime() < Date.now() ? new Date() : d);
    const [selectedDate, setSelectedDate] = useState<Date | null>(
      value ? clampPastToNow(new Date(value)) : null
    );
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef<any>(null);

    // Helpers for time constraints
    const getStartOfDayFor = (base: Date) => {
      const d = new Date(base);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const getEndOfDayFor = (base: Date) => {
      const d = new Date(base);
      d.setHours(23, 59, 59, 999);
      return d;
    };

    const roundUpToIntervalMinutes = (date: Date, intervalMinutes: number) => {
      const d = new Date(date);
      const remainder = d.getMinutes() % intervalMinutes;
      if (remainder !== 0) {
        d.setMinutes(d.getMinutes() + (intervalMinutes - remainder), 0, 0);
      } else {
        d.setSeconds(0, 0);
      }
      return d;
    };

    const isSameDay = (a: Date, b: Date) =>
      a.toDateString() === b.toDateString();

    const setTimeFromSource = (targetDate: Date, timeSource: Date) => {
      const d = new Date(targetDate);
      d.setHours(timeSource.getHours(), timeSource.getMinutes(), 0, 0);
      return d;
    };

    const isDefaultMidnightTime = (d: Date) =>
      d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;

    const getMinSelectableTimeFor = (currentSelection: Date | null) => {
      const now = new Date();
      if (currentSelection && isSameDay(currentSelection, now)) {
        return roundUpToIntervalMinutes(now, 15);
      }
      return getStartOfDayFor(currentSelection ?? now);
    };

    const clampToNowIfToday = (candidate: Date) => {
      const now = new Date();
      if (isSameDay(candidate, now) && candidate.getTime() < now.getTime()) {
        return getMinSelectableTimeFor(candidate);
      }
      return candidate;
    };

    // Re-seed from the value prop only while the picker is CLOSED. The
    // value can legitimately change under an open picker — a group pick
    // moves the cohort and the header's summary flips to the post left
    // behind — and re-seeding mid-interaction swapped the calendar's day
    // for that other post's, so the next time click built on the wrong
    // day and moved the wrong post. Once closed (a time pick auto-closes,
    // or a click outside), the picker catches up with whatever the value
    // is now.
    useEffect(() => {
      if (isOpen) return;
      if (value) {
        setTimeout(() => setSelectedDate(clampPastToNow(new Date(value))), 0);
      } else {
        setTimeout(() => setSelectedDate(null), 0);
      }
    }, [value, isOpen]);

    // Expose methods to parent component via ref
    useImperativeHandle(
      ref,
      () =>
      ({
        focus: () => {
          setIsOpen(true);
          datePickerRef.current?.setFocus();
        },
        showPicker: () => {
          setIsOpen(true);
          datePickerRef.current?.setOpen(true);
        },
        blur: () => {
          setIsOpen(false);
          datePickerRef.current?.setOpen(false);
        },
      } as any)
    );

    const handleDateChange = (date: Date | null) => {
      if (!date) {
        setSelectedDate(date);
        return;
      }

      const isDayChange = !selectedDate || !isSameDay(selectedDate, date);
      let candidate: Date = new Date(date);

      if (isDayChange) {
        if (dateOnlyDefaultTime === "startOfDay") {
          // Lower-bound pickers ("Start from"): a day pick means "start from
          // this day", so reset to midnight even when a previous/prefilled
          // time exists (clampToNowIfToday handles today). Explicit time
          // clicks are same-day changes and stay untouched below.
          candidate = getStartOfDayFor(candidate);
        } else if (selectedDate) {
          // Preserve previously chosen time across day changes
          candidate = setTimeFromSource(candidate, selectedDate);
        } else if (isDefaultMidnightTime(candidate)) {
          // First selection with date-only click: choose recommended time
          candidate = getBestRecommendedTime(candidate);
        } else {
          // First selection with time chosen: keep as is
        }
      } else {
        // Same day: treat as time change; keep as is
      }

      const adjusted = clampToNowIfToday(candidate);
      setSelectedDate(adjusted);
      onChange(adjusted);
    };
    const resolvedPopperPlacement =
      popperPlacement === "auto" ||
      popperPlacement === "auto-start" ||
      popperPlacement === "auto-end"
        ? undefined
        : popperPlacement;

    return (
      <ReactDatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={handleDateChange}
        onBlur={() => {
          onBlur?.();
        }}
        open={isOpen}
        onClickOutside={() => setIsOpen(false)}
        onCalendarClose={() => setIsOpen(false)}
        showTimeSelect
        timeFormat="h:mm aa"
        timeIntervals={15}
        dateFormat="dd/MM/yyyy HH:mm"
        minDate={new Date()}
        minTime={getMinSelectableTimeFor(selectedDate)}
        maxTime={getEndOfDayFor(selectedDate ?? new Date())}
        // Above popovers (PopoverPannel's z), as it can open from one.
        popperClassName="!z-[2147483647]"
        popperProps={popperStrategy ? { strategy: popperStrategy } : undefined}
        portalId={portalId}
        shouldCloseOnSelect
        wrapperClassName={clsx(
          "opacity-0 pointer-events-none absolute inset-0",
          rest.className
        )}
        renderCustomHeader={(props) => <CustomHeader {...props} />}
        locale="en-US"
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 2)}
        popperPlacement={
          resolvedPopperPlacement ??
          (windowWidth < 768 ? "bottom-start" : "bottom")
        }
      />
    );
  }
);

DatePicker.displayName = "DatePicker";

export default DatePicker;
